/** @jsx isolateComponent */

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Colors,
  Button,
  Typography,
  Spacer,
  TooltipWrapper,
  Icon,
} from '@zapier/design-system';
import { Checkbox } from '@zapier/design-system-beta';

import Surface from '@zapier/product-get-help/src/components/shared/Surface';

import Fields, { Field } from './FormFields/Fields';

const RebrandStyles = {
  additionalInfo: {
    ...Typography.paragraph3,
    color: Colors.neutral800,
    marginTop: '0px',
    marginBottom: '20px',
  },
  criticalIssueInstructions: {
    ...Typography.smallPrint1,
    fontSize: '12px',
    color: Colors.neutral700,
    display: 'flex',
  },
  buttonWrapper: {
    marginTop: '40px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  iconButton: {
    marginLeft: '4px',
    verticalAlign: 'middle',
    display: 'inline-flex',
    border: 'none',
  },
};

export type FormState = 'initial' | 'readyToSubmit' | 'submitting';

type EmailStepProps = {
  /** An array of fields to render in the form. */
  fields: Array<Field>;
  /** What state the form is in.  */
  formState: FormState;
  /** Whether the user can see and select the Critical Issue checkbox */
  isCriticalTicketEligible: boolean;
  /** Whether the issue is critical */
  isCriticalIssue: boolean;
  /** Function to set the critical issue state */
  setIsCriticalIssue: (isCritical: boolean) => void;
  /** Function to update the meta data */
  setMeta: (key: string, value: any) => void;
};

/**
 * Renders a form with fields and a submit button.
 */
const EmailStep = ({
  fields,
  formState,
  isCriticalTicketEligible,
  isCriticalIssue,
  setIsCriticalIssue,
  setMeta,
}: EmailStepProps) => {
  const handleCheckboxChange = () => {
    const newValue = !isCriticalIssue;
    setIsCriticalIssue(newValue);
    setMeta('is_critical_issue', newValue);
  };

  return (
    <Surface title="Tell us about your issue">
      <div css={RebrandStyles.additionalInfo}>
        Please note the apps you are using and what you are hoping to
        accomplish. If you have an example that illustrates your question,
        please provide that as well.
      </div>
      <Spacer height={10} />
      <Fields fields={fields} />
      <Spacer height={10} />
      {isCriticalTicketEligible && (
        <div>
          <Checkbox
            defaultSelected={isCriticalIssue}
            onChange={handleCheckboxChange}
          >
            Critical Issue
          </Checkbox>
          <div css={RebrandStyles.criticalIssueInstructions}>
            Check this box only if your issue needs urgent attention because it
            disrupts your business operations.
            <TooltipWrapper
              allowMultilineTooltip={true}
              content="For example, critical issues include functionality loss, data loss, or a possible system outage."
            >
              {({ childProps }) => (
                <Icon
                  {...childProps}
                  aria-label="Information"
                  css={RebrandStyles.iconButton}
                  name="alertInfo"
                  size={16}
                />
              )}
            </TooltipWrapper>
          </div>
        </div>
      )}
      <div css={RebrandStyles.buttonWrapper}>
        <Button disabled={formState !== 'readyToSubmit'} type="submit">
          {formState === 'submitting' ? 'Sending...' : 'Send'}
        </Button>
      </div>
    </Surface>
  );
};

export default EmailStep;
